/**
 * <LinkExperienceModal> - Iter 9.40 Pass I
 *
 * Logged-in users opening /preference-share/:cardId can:
 *   (1) link an existing experience to this card,
 *   (2) create a new experience inline and link it,
 *   (3) group this card under an occasion (existing or new).
 *
 * Writes to:
 *   GET  /api/me/experiences-for-link (typeahead)
 *   POST /api/preference-share/:cardId/link { action, ... }
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Sparkles, CalendarHeart, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LinkExperienceModal({ open, onOpenChange, cardId, category, onLinked }) {
  const [tab, setTab] = useState("existing"); // 'existing' | 'new' | 'occasion'
  const [q, setQ] = useState("");
  const [experiences, setExperiences] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newExpName, setNewExpName] = useState("");
  const [newOccName, setNewOccName] = useState("");
  const [selectedOcc, setSelectedOcc] = useState("");

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/me/experiences-for-link${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: auth() })
      .then((r) => r.ok ? r.json() : { experiences: [], occasions: [] })
      .then((d) => { setExperiences(d.experiences || []); setOccasions(d.occasions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, q]);

  const submit = async (body) => {
    const res = await fetch(`${API}/preference-share/${cardId}/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const d = await res.json();
      toast.success("Linked!");
      onLinked?.(d);
      onOpenChange?.(false);
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Failed to link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="link-experience-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-purple-500" /> Link or group this card
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b mb-3" data-testid="link-modal-tabs">
          <button
            onClick={() => setTab("existing")}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "existing" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500"}`}
            data-testid="link-tab-existing"
          >
            Link existing
          </button>
          <button
            onClick={() => setTab("new")}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "new" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500"}`}
            data-testid="link-tab-new"
          >
            Create new
          </button>
          <button
            onClick={() => setTab("occasion")}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "occasion" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500"}`}
            data-testid="link-tab-occasion"
          >
            Group under occasion
          </button>
        </div>

        {tab === "existing" && (
          <div className="space-y-3" data-testid="link-existing-panel">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search your experiences..."
                className="pl-9"
                data-testid="link-search-input"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {loading ? (
                <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-gray-400" /></div>
              ) : experiences.length > 0 ? (
                experiences.map((e) => (
                  <button
                    key={e.experience_id}
                    onClick={() => submit({ action: "link_experience", experience_id: e.experience_id })}
                    className="w-full text-left p-2 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50"
                    data-testid={`link-experience-${e.experience_id}`}
                  >
                    <p className="text-sm font-medium">{e.name || "Untitled"}</p>
                    <p className="text-xs text-gray-500 capitalize">{e.pilot_category} · {e.status || "pending"}</p>
                  </button>
                ))
              ) : (
                <p className="py-6 text-center text-xs text-gray-400">No experiences yet. Create one.</p>
              )}
            </div>
          </div>
        )}

        {tab === "new" && (
          <div className="space-y-3" data-testid="link-new-panel">
            <div>
              <Label className="text-xs">Experience name</Label>
              <Input
                value={newExpName}
                onChange={(e) => setNewExpName(e.target.value)}
                placeholder={`e.g. My ${category || 'experience'} session`}
                data-testid="link-new-name-input"
              />
            </div>
            <Button
              className="w-full"
              disabled={!newExpName.trim()}
              onClick={() => submit({ action: "create_experience", experience_name: newExpName, pilot_category: category })}
              data-testid="link-new-submit"
            >
              <Plus className="w-4 h-4 mr-1" /> Create + link
            </Button>
          </div>
        )}

        {tab === "occasion" && (
          <div className="space-y-3" data-testid="link-occasion-panel">
            {occasions.length > 0 && (
              <div>
                <Label className="text-xs">Pick an existing occasion</Label>
                <Select value={selectedOcc} onValueChange={setSelectedOcc}>
                  <SelectTrigger data-testid="link-occasion-select"><SelectValue placeholder="Select occasion" /></SelectTrigger>
                  <SelectContent>
                    {occasions.map((o) => (
                      <SelectItem key={o.occasion_id} value={o.occasion_id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  disabled={!selectedOcc}
                  onClick={() => submit({ action: "group_occasion", occasion_id: selectedOcc })}
                  data-testid="link-occasion-submit-existing"
                >
                  <CalendarHeart className="w-4 h-4 mr-1" /> Group under this occasion
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs">Or create a new occasion</Label>
              <Input
                value={newOccName}
                onChange={(e) => setNewOccName(e.target.value)}
                placeholder="e.g. Maya's Wedding Week"
                data-testid="link-occasion-new-input"
              />
              <Button
                className="w-full mt-2"
                disabled={!newOccName.trim()}
                onClick={() => submit({ action: "group_occasion", occasion_name: newOccName })}
                data-testid="link-occasion-submit-new"
              >
                <Plus className="w-4 h-4 mr-1" /> Create occasion + group
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
